import React, { useEffect, useState, useRef } from 'react';
import { AiSummarizeForNotesAPI } from '../../../api/create';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { IoCopyOutline } from 'react-icons/io5';
import { BsCheckCircleFill } from 'react-icons/bs';
import { FiLoader } from 'react-icons/fi';
import MarkdownBlock from '../../../components/mark-down';
import AIIcon from '../../../assets/icons/AIIcon.png';
import { useAISummarizeWebSocket } from '../../../hooks/useAISummarizeWebSocket';
import { toast } from 'react-toastify';

interface NotesAISummarizeModalProps {
    show: boolean;
    patientId: string | number;
    selectedNotesIds: string[];
    todoExtraction?: boolean;
    onClose: () => void;
}

const NotesAISummarizeModal: React.FC<NotesAISummarizeModalProps> = ({
    show,
    patientId,
    selectedNotesIds,
    todoExtraction = false,
    onClose,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [summary, setSummary] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [copied, setCopied] = useState<boolean>(false);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [progressPercent, setProgressPercent] = useState<number>(0);
    const [progressMessage, setProgressMessage] = useState<string>('');

    // WebSocket hook
    const {
        lastMessage,
        connectionStatus,
        connect,
        disconnect
    } = useAISummarizeWebSocket();

    // Connection timeout tracking
    const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Handle WebSocket messages
    useEffect(() => {
        if (lastMessage) {

            // Update progress message and percentage
            setProgressMessage(lastMessage.message);

            if (lastMessage.progress_percent !== undefined
                && lastMessage.progress_percent !== null) {
                setProgressPercent(lastMessage.progress_percent);
                console.log(`[Progress] ${lastMessage.progress_percent}% - ${lastMessage.message}`);
            }

            // Handle different status types
            if (lastMessage.status === 'queued') {
                toast.info('Notes queued for processing...', { autoClose: 2000 });
            }

            if (lastMessage.status === 'processing') {
                const progress = lastMessage.progress_percent;
                if (progress === 20) {
                    toast.info('Loading notes...', { autoClose: 2000 });
                } else if (progress === 50) {
                    toast.info('Analyzing with medical AI...', { autoClose: 2000 });
                } else if (progress === 80) {
                    toast.info('Saving summary...', { autoClose: 2000 });
                }
            }

            if (lastMessage.status === 'completed' && lastMessage.data) {
                // Clear any pending timeouts
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                if (lastMessage.data.summary) {
                    setSummary(lastMessage.data.summary);
                }
                setLoading(false);
                toast.success('Summary generated successfully!', { autoClose: 3000 });
                disconnect();
            }

            if (lastMessage.status === 'error') {
                // Clear any pending timeouts
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                setLoading(false);
                setError(lastMessage.message || 'An error occurred while processing');
                console.error('[Error]', lastMessage.message);
                toast.error(lastMessage.message || 'An error occurred while processing',
                    { autoClose: 5000 });
                disconnect();
            }
        }
    }, [lastMessage, disconnect]);

    // Monitor WebSocket connection status with timeout
    useEffect(() => {
        if (loading && connectionStatus === 'connected') {
            // Set timeout for processing (3 minutes max)
            connectionTimeoutRef.current = setTimeout(() => {
                toast.error('Processing timeout. The operation took too long.');
                setLoading(false);
                setProgressMessage('Processing timeout');
                disconnect();
            }, 3 * 60 * 1000); // 3 minutes
        }

        return () => {
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
                connectionTimeoutRef.current = null;
            }
        };
    }, [connectionStatus, loading, disconnect]);

    useEffect(() => {
        if (show && selectedNotesIds.length > 0) {
            fetchAISummary();
        }

        return () => {
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
            }
            disconnect();
        };
    }, [show, selectedNotesIds]);

    const fetchAISummary = async () => {
        setLoading(true);
        setError('');
        setSummary('');
        setCopied(false);
        setProgressPercent(0);
        setProgressMessage('Connecting to processing service...');

        try {
            // STEP 1: Generate session_id on frontend
            const sessionId = crypto.randomUUID();
            setCurrentSessionId(sessionId);
            console.log('[Frontend] Generated session_id:', sessionId);

            // STEP 2: Connect WebSocket FIRST (before API call)
            // This is critical! Backend waits for WebSocket to be ready
            console.log('[Frontend] Connecting WebSocket...');
            setProgressMessage('Establishing secure connection...');

            try {
                await connect(sessionId);
                console.log('[Frontend] ✓ WebSocket connected successfully');
                setProgressMessage('Connection established. Queuing request...');
            } catch (wsError) {
                console.error('[Frontend] ✗ WebSocket connection failed:', wsError);
                toast.error('Failed to connect to processing service');
                setError('Failed to connect to processing service. Please try again.');
                setLoading(false);
                return;
            }

            // STEP 3: Now call the API with the same session_id
            // Backend will wait for WebSocket ready signal before sending messages
            console.log('[Frontend] Calling API with session_id:', sessionId);
            const response = await AiSummarizeForNotesAPI({
                patient_id: String(patientId),
                notes_ids: selectedNotesIds,
                todo_extraction: todoExtraction,
                session_id: sessionId,  // Pass our pre-generated session_id
            });

            if (response?.success) {
                console.log('[Frontend] ✓ API request queued successfully');
                // WebSocket will handle all progress updates
                // Don't check for cached response here - let WebSocket deliver it
            } else if (response?.result?.summary) {
                // Fallback for old API response format
                setSummary(response.result.summary);
                setLoading(false);
                toast.success('Summary retrieved!', { autoClose: 2000 });
            } else {
                setError('Failed to queue summary request. Please try again.');
                setLoading(false);
                toast.error('Failed to queue request');
                disconnect();
            }
        } catch (err: any) {
            console.error('[Frontend] ✗ API error:', err);
            setError(err?.message || 'An error occurred while fetching the summary.');
            setLoading(false);
            toast.error(err?.message || 'An error occurred');
            disconnect();
        }
    };

    const handleCopy = () => {
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    if (!show) return null;

    return (
        <>
            <style>{`
                @keyframes shimmer {
                    0% {
                        background-position: -200% 0;
                    }
                    100% {
                        background-position: 200% 0;
                    }
                }
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-shimmer {
                    animation: shimmer 2s infinite;
                    background: linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255, 255, 255, 0.4) 50%,
                        transparent 100%
                    );
                    background-size: 200% 100%;
                }
                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-out;
                }
            `}</style>
            <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
                <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg max-h-[80vh] flex flex-col">
                {/* Fixed Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-teal-50 via-cyan-50 to-emerald-50">
                    <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded-xl shadow-md border-2 border-teal-200">
                            <img src={AIIcon} alt="Notes" className="w-10 h-10" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">
                            ScribeSmart <span
                                className="bg-gradient-to-r from-teal-600 to-cyan-600
                                bg-clip-text text-transparent"
                            >{todoExtraction ? 'Todo Extraction' : 'Notes Summary'}</span>
                        </h2>
                    </div>
                    {summary && !loading && (
                        <CopyToClipboard
                            text={summary}
                            onCopy={handleCopy}
                        >
                            <button
                                className="border border-teal-300 text-teal-700 bg-white py-2 px-3 rounded-lg font-[600] text-xs text-center cursor-pointer hover:bg-teal-50 transition-colors duration-200"
                            >
                                <span className="flex items-center justify-center">
                                    Copy to Clipboard &nbsp;&nbsp;{' '}
                                    {copied ? (
                                        <BsCheckCircleFill size={15} color="#14b8a6" />
                                    ) : (
                                        <IoCopyOutline size={15} />
                                    )}
                                </span>
                            </button>
                        </CopyToClipboard>
                    )}
                </div>

                {/* Scrollable Body */}
                <div className="overflow-y-auto h-[calc(100vh-200px)] bg-gray-50">
                    {loading && (
                        <div className="p-6 animate-fadeIn">
                            {/* Header with animated icon */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="relative">
                                    <FiLoader
                                        className="animate-spin text-teal-600"
                                        size={32}
                                    />
                                    <div
                                        className="absolute inset-0 bg-teal-200
                                        rounded-full animate-ping opacity-75"
                                    ></div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Processing Notes
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        Analyzing {selectedNotesIds.length} clinical note
                                        {selectedNotesIds.length > 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>

                            {/* Animated Progress Message with Typing Dots */}
                            <div
                                className="mb-4 p-4 bg-white rounded-lg border
                                border-teal-100 shadow-sm"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {progressMessage || 'Processing'}
                                    </span>
                                    <span className="flex gap-1">
                                        <span
                                            className="w-1.5 h-1.5 bg-teal-600 rounded-full
                                            animate-bounce"
                                            style={{ animationDelay: '0ms' }}
                                        ></span>
                                        <span
                                            className="w-1.5 h-1.5 bg-teal-600 rounded-full
                                            animate-bounce"
                                            style={{ animationDelay: '150ms' }}
                                        ></span>
                                        <span
                                            className="w-1.5 h-1.5 bg-teal-600 rounded-full
                                            animate-bounce"
                                            style={{ animationDelay: '300ms' }}
                                        ></span>
                                    </span>
                                </div>
                            </div>

                            {/* Progress Bar with Shimmer Effect */}
                            <div className="mb-6">
                                <div
                                    className="flex justify-between text-sm text-gray-600
                                    mb-2"
                                >
                                    <span className="font-semibold">Progress</span>
                                    <span
                                        className="font-bold text-teal-600
                                        transition-all duration-300"
                                    >
                                        {progressPercent}%
                                    </span>
                                </div>
                                <div className="relative">
                                    <div
                                        className="w-full bg-gray-200 rounded-full h-5
                                        overflow-hidden shadow-inner"
                                    >
                                        <div
                                            className="bg-gradient-to-r from-teal-600
                                            via-cyan-500 to-teal-600 h-5 rounded-full
                                            transition-all duration-700 ease-out
                                            flex items-center justify-end pr-2
                                            relative overflow-hidden"
                                            style={{ width: `${progressPercent}%` }}
                                        >
                                            {/* Shimmer effect */}
                                            <div
                                                className="absolute inset-0 animate-shimmer"
                                            ></div>
                                            {progressPercent > 15 && (
                                                <span
                                                    className="text-xs text-white font-bold
                                                    relative z-10"
                                                >
                                                    {progressPercent}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Processing Steps Indicators */}
                            <div className="mb-6 space-y-3">
                                <div
                                    className={`flex items-center gap-3 p-3 rounded-lg
                                    transition-all duration-500 ${progressPercent >= 10
                                            ? 'bg-teal-50 border-l-4 border-teal-500'
                                            : 'bg-gray-100 border-l-4 border-gray-300'
                                        }`}
                                >
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center
                                        justify-center ${progressPercent >= 10
                                                ? 'bg-teal-500 text-white'
                                                : 'bg-gray-300 text-gray-600'
                                            }`}
                                    >
                                        {progressPercent >= 20 ? '✓' : '1'}
                                    </div>
                                    <span
                                        className={`text-sm font-medium ${progressPercent >= 10
                                                ? 'text-teal-800'
                                                : 'text-gray-600'
                                            }`}
                                    >
                                        Loading medical notes
                                    </span>
                                </div>

                                <div
                                    className={`flex items-center gap-3 p-3 rounded-lg
                                    transition-all duration-500 ${progressPercent >= 50
                                            ? 'bg-teal-50 border-l-4 border-teal-500'
                                            : 'bg-gray-100 border-l-4 border-gray-300'
                                        }`}
                                >
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center
                                        justify-center ${progressPercent >= 50
                                                ? 'bg-teal-500 text-white'
                                                : 'bg-gray-300 text-gray-600'
                                            }`}
                                    >
                                        {progressPercent >= 80 ? '✓' : '2'}
                                    </div>
                                    <span
                                        className={`text-sm font-medium ${progressPercent >= 50
                                                ? 'text-teal-800'
                                                : 'text-gray-600'
                                            }`}
                                    >
                                        Analyzing clinical content
                                    </span>
                                </div>

                                <div
                                    className={`flex items-center gap-3 p-3 rounded-lg
                                    transition-all duration-500 ${progressPercent >= 80
                                            ? 'bg-teal-50 border-l-4 border-teal-500'
                                            : 'bg-gray-100 border-l-4 border-gray-300'
                                        }`}
                                >
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center
                                        justify-center ${progressPercent >= 80
                                                ? 'bg-teal-500 text-white'
                                                : 'bg-gray-300 text-gray-600'
                                            }`}
                                    >
                                        {progressPercent === 100 ? '✓' : '3'}
                                    </div>
                                    <span
                                        className={`text-sm font-medium ${progressPercent >= 80
                                                ? 'text-teal-800'
                                                : 'text-gray-600'
                                            }`}
                                    >
                                        Generating summary
                                    </span>
                                </div>
                            </div>

                            {/* Connection Status with Animation */}
                            <div
                                className="flex items-center justify-between p-3
                                bg-white rounded-lg border border-gray-200"
                            >
                                <div className="flex items-center gap-2 text-sm">
                                    <span
                                        className={`w-2.5 h-2.5 rounded-full ${connectionStatus === 'connected'
                                                ? 'bg-green-500 animate-pulse'
                                                : connectionStatus === 'connecting'
                                                    ? 'bg-yellow-500 animate-ping'
                                                    : connectionStatus === 'error'
                                                        ? 'bg-red-500'
                                                        : 'bg-gray-400'
                                            }`}
                                    ></span>
                                    <span className="font-medium text-gray-700">
                                        {connectionStatus === 'connected'
                                            ? 'Processing Service Connected'
                                            : connectionStatus === 'connecting'
                                                ? 'Connecting to Processing Service...'
                                                : connectionStatus === 'error'
                                                    ? 'Connection Error'
                                                    : 'Disconnected'}
                                    </span>
                                </div>
                                {connectionStatus === 'connected' && (
                                    <span className="text-xs text-green-600 font-semibold">
                                        Active
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {error && !loading && (
                        <div className="px-6 py-4">
                            <div
                                className="p-4 bg-red-50 border border-red-200
                                rounded-lg"
                            >
                                <p className="font-semibold text-red-800 mb-1">Error</p>
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        </div>
                    )}

                    {summary && !loading && (
                        <div className="px-6 py-4 text-gray-800 leading-relaxed">
                            <MarkdownBlock mark_down_content={summary} />
                        </div>
                    )}
                </div>

                {/* Fixed Footer */}
                {!loading && (
                    <div className="flex justify-end p-6 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="text-[#031227] bg-white hover:bg-[#F5F5F5] disabled:bg-white disabled:text-[#AAADB1]  px-[12px] py-[10px] text-[14px] font-semibold leading-[17px] border-[#e0d9d9] border-[1px] rounded-lg"
                        >
                            Close
                        </button>
                    </div>
                )}
                </div>
            </div>
        </>
    );
};

export default NotesAISummarizeModal;